import {MetricValue} from 'common/src/modules/analytics/analytics.types';
import _isNaN from 'lodash/isNaN';
import dashboardsApi from 'common/src/modules/dashboards/dashboards.api';
import {Dashboard, DashboardWidget} from 'common/src/modules/dashboards/dashboards.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import _find from 'lodash/find';
import {z} from 'zod';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';
import {inlineWidgetSchema} from '../../../copilotAnalytics.schemas';
import checkWidgetExistsTool from './checkWidgetExistsTool';
import createDashboardTool from './createDashboardTool';
import createWidgetTool from './createWidgetTool';
import highlightWidgetTool from './highlightWidgetTool';

const inputSchema = z.object({
  widget: inlineWidgetSchema
    .describe(
      'The inline widget configuration that should be persisted as a dashboard widget. Must be taken directly from the output of an analytics tool (for example GetCampaignTopPerforming).',
    )
    .nullable()
    .optional(),
});

function persistInlineWidgetToCampaignDashboardTool(store: CLStore) {
  async function execute({
    input,
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const {widget} = input;

    if (!widget) {
      return toolError('Inline widget configuration is required to persist a widget.');
    }

    const {
      content: {metrics: aiMetrics, dimensions, type, breakdownByTime},
    } = widget;

    const metrics = aiMetrics as MetricValue[];

    const [dimension] = dimensions;

    const {
      campaignPage: {
        insightsPage: {dashboards},
      },
    } = store.getState();

    const writableDashboards = dashboards.filter((dashboard: Dashboard) => !dashboard.readOnly);

    let selectedDashboardId: number | null = null;

    if (!writableDashboards.length) {
      const createDashboard = createDashboardTool(store);

      const createDashboardResult = await createDashboard.execute({
        input: {},
        toolUtils,
        callChain,
      });

      const createdDashboardId = Number(
        createDashboardResult.content.replace(/^\D*(\d+)\D*$/, '$1'),
      );

      if (_isNaN(createdDashboardId)) {
        return toolError('Failed to create a new dashboard for this campaign.');
      }

      selectedDashboardId = createdDashboardId;
    } else if (writableDashboards.length === 1) {
      selectedDashboardId = writableDashboards[0].id;
    } else {
      for (const dashboard of writableDashboards) {
        const shouldUseDashboard = await toolUtils.askUserConfirmation({
          title: 'Choose dashboard for this widget',
          text: `Do you want to add or update this widget in the dashboard "${dashboard.name}"?`,
          okLabel: 'Yes, use this dashboard',
          cancelLabel: 'No, show another option',
        });

        if (shouldUseDashboard) {
          selectedDashboardId = dashboard.id;

          break;
        }
      }

      if (!selectedDashboardId) {
        return toolResult('No dashboard selected. Inline widget was not persisted.');
      }
    }

    const dashboardId = selectedDashboardId;

    const checkWidgetExists = checkWidgetExistsTool(store);
    const createWidget = createWidgetTool(store);
    const highlightWidget = highlightWidgetTool(store);

    const existsResult = await checkWidgetExists.execute({
      input: {
        dashboardId,
        dimension,
        metrics: metrics as MetricValue[],
        breakdownByTime,
        type,
        limit: null,
        eventType: widget.content.eventType,
        withTrends: widget.content.withTrends,
      },
      toolUtils,
      callChain,
    });

    if (JSON.parse(existsResult.content) === true) {
      const widgets = await dashboardsApi.getWidgets(dashboardId);

      const existingWidget = _find(widgets, {
        metrics,
        dimensions: [dimension],
      }) as DashboardWidget | undefined;

      if (!existingWidget) {
        return toolError('Widget was reported as existing but could not be found.');
      }

      await highlightWidget.execute({
        input: {
          dashboardId,
          widgetId: existingWidget.id,
        },
        toolUtils,
        callChain,
      });

      return toolResult('Existing widget highlighted.');
    }

    const createResult = await createWidget.execute({
      input: {
        dashboardId,
        dimension,
        metrics: metrics as MetricValue[],
        breakdownByTime,
        type,
        limit: null,
        eventType: widget.content.eventType,
        withTrends: widget.content.withTrends,
      },
      toolUtils,
      callChain,
    });

    const widgetId = Number(JSON.parse(createResult.content));

    if (_isNaN(widgetId)) {
      return toolError('Failed to create dashboard widget from inline widget.');
    }

    await highlightWidget.execute({
      input: {
        dashboardId,
        widgetId,
      },
      toolUtils,
      callChain,
    });

    return toolResult('New widget created and highlighted.');
  }

  return tool({
    name: CampaignAnalyticsToolName.PersistInlineWidgetToDashboard,
    displayName: 'Persisting inline widget to dashboard',
    description:
      'Given an inline widget configuration, checks if a matching dashboard widget exists and highlights it, or creates a new widget and highlights it.',
    execute,
    inputSchema,
  });
}

export default persistInlineWidgetToCampaignDashboardTool;
